import { useFavoriteLibraries } from '@lib/hooks/useFavoriteLibraries';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { openAuthedChatUrl } from '@lib/network/chats';
import {
  getPodcastCreateUrl,
  getPodcastProjectUrl,
  getPodcastsLandingUrl,
  listPodcastInstances,
  PodcastInstance,
} from '@lib/network/podcasts';
import { getLibraryUrl } from '@lib/utils/url';
import { Skeleton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { AppPage } from '@pages/AppPage';
import {
  AddMediaModal,
  AddMediaModalRef,
} from '@pages/Libraries/components/AddMediaModal/AddMediaModal';
import {
  emptyLibraryDisabledReason,
  LibraryDestination,
  LibrarySwitch,
} from '@pages/Search/components/SearchBar/components/LibrarySwitch/LibrarySwitch';
import { debugStore } from '@stores/Debug';
import { LibraryStore } from '@stores/Library/Library';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CapabilityCard } from './components/CapabilityCard';
import {
  AddMediaPreview,
  AgentPreview,
  PodcastPreview,
  SearchPreview,
} from './components/CapabilityPreviews';
import { ContinueRow } from './components/ContinueRow';
import { WorkspaceHero } from './components/WorkspaceHero';
import css from './WorkspacePage.module.scss';

// Whether the chat web app is configured at all. The deep-link helpers
// (`getPodcastsLandingUrl`, `getPodcastCreateUrl`, etc.) return '' when
// `CHAT_URL` isn't set, so we use that as a stable boolean for gating
// rendering. The actual URLs are computed inline at click/render time
// so they pick up a short-lived exchange token via `openAuthedChatUrl`.
const CHAT_CONFIGURED = !!process.env.CHAT_URL;

type SignpostId = Parameters<typeof trackingStore.workspaceSignpostClicked>[0];
type SignpostProps = Parameters<typeof trackingStore.workspaceSignpostClicked>[1];

function usePodcastProjects(skip: boolean): {
  instances: PodcastInstance[] | null;
  error: Error | null;
} {
  const [instances, setInstances] = useState<PodcastInstance[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (skip) {
      setInstances(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    listPodcastInstances({ signal: controller.signal })
      .then((data) => setInstances(data))
      .catch((e) => {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch podcast projects:', e);
        setError(e);
        setInstances([]);
      });
    return () => controller.abort();
  }, [skip]);

  return { instances, error };
}

// Debug-only: synthetic podcast projects (no library/media ids → gradient
// placeholders) so the continue row can be previewed at any count from the DebugTab.
// `PodcastInstance` only requires `_id`, so a plain literal satisfies it — no cast.
const makeFakePodcastClips = (count: number): PodcastInstance[] =>
  Array.from({ length: count }, (_, i) => ({
    _id: `debug-clip-${i}`,
    name: `Debug clip ${i + 1}`,
    setup: { title: `Debug podcast project ${i + 1}` },
  }));

// Placeholder for the capability row while library stats load, so the real cards swap in
// without shoving the layout around.
const CapabilitySkeletons = () => (
  <>
    {Array.from({ length: 4 }, (_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height="100%"
        sx={{ borderRadius: '20px', gridRow: 'span 2' }}
      />
    ))}
  </>
);

/** Shown in place of the workspace until the user verifies their email. */
const UnverifiedNotice = () => (
  <div className={css.ftueContainer}>
    <div className={`${css.workspaceCard} ${css.unverifiedEmail}`}>
      To enable creating and uploading libraries, please verify your email.
      <ActionButton onClick={() => userStore.resendEmailVerification()}>
        Resend verification email
      </ActionButton>
    </div>
  </div>
);

/**
 * The workspace for verified users: what StarZero does (hero), what you were working on
 * (returning users only), and a card per thing you can start.
 */
const WorkspaceHome = observer(() => {
  const libraryManager = useLibraryManager();
  const navigate = useNavigate();
  // Shared with every other library-verb surface, so these cards inherit the transcript-only
  // mode rule instead of re-deriving a capability-blind default.
  const { findMoments, workWithAgent } = useLibraryVerbs();
  const addMediaModalRef = useRef<AddMediaModalRef>(null);

  useEffect(() => {
    if (!libraryManager.defaultLibrary && userStore.isLoggedIn) {
      libraryManager.forceFetchDefaultLibrary();
    }
    // MobX observables (userStore.isLoggedIn) are tracked by observer(), not by React deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryManager.defaultLibrary]);

  const demoLibrary = libraryManager.demoLibrary;
  const defaultLibrary = libraryManager.defaultLibrary;
  const isFirstTimeUser = libraryManager.isFirstTimeUser;
  const favoriteLibraries = useFavoriteLibraries();
  const { instances: fetchedPodcastProjects } = usePodcastProjects(!CHAT_CONFIGURED);
  // Debug override: pretend to have N projects (DebugTab → "Fake Workspace podcast
  // clips") to preview every count of the continue row without real data. `null` uses
  // the real fetch.
  const podcastProjects =
    debugStore.fakePodcastClipCount != null
      ? makeFakePodcastClips(debugStore.fakePodcastClipCount)
      : fetchedPodcastProjects;
  const clipsLoading = CHAT_CONFIGURED && podcastProjects === null;
  // A cancelled run has nothing to pick back up, so it doesn't earn a continue slot.
  const recentClips = (podcastProjects ?? [])
    .filter((project) => project.status !== 'cancelled')
    .slice(0, 3);
  const recentFavorites = [...favoriteLibraries]
    .sort((a, b) => (b.data?.updatedAt ?? 0) - (a.data?.updatedAt ?? 0))
    .slice(0, 3);
  const showContinue =
    libraryManager.areLibraryStatsLoaded &&
    !isFirstTimeUser &&
    (clipsLoading || recentClips.length > 0 || recentFavorites.length > 0);

  // The library the agent + search cards act on. Defaults to the most recent library with
  // ready media (null = follow `latestLibrary`), and to the demo library for someone who has
  // none yet, so a newcomer can try both before uploading anything. The picker on the agent
  // card retargets both. Stored as an id so it tracks live store updates and survives the
  // latest-library recomputing.
  const [pickedLibraryId, setPickedLibraryId] = useState<string | null>(null);
  const activeLibrary =
    (pickedLibraryId ? libraryManager.libraryById(pickedLibraryId) : null) ??
    libraryManager.latestLibrary ??
    demoLibrary ??
    null;
  const activeIsDemo = !!activeLibrary?.data?.system?.demo;
  // The cards fell back to the demo while a batch of the user's media is still processing.
  // Read from the live batches, not library stats: nothing refetches stats when processing
  // finishes, so a stats-based check would keep claiming "processing" after the media is ready.
  const awaitingOwnMedia =
    activeIsDemo && !pickedLibraryId && notificationStore.activeMediaNotifications.length > 0;
  const demoCount = demoLibrary?.stats?.breakdown?.completed?.count;

  // ── Signpost analytics ─────────────────────────────────────────────────────
  // Every click reports the surface's title, its rendered position, and the layout variant
  // alongside the action. Position + variant travel together: which surfaces appear differs
  // between the first-time and returning layouts, so a bare position is meaningless without
  // the variant.
  const layoutVariant: 'first_time' | 'returning' = isFirstTimeUser ? 'first_time' : 'returning';
  const signpostTitles: Record<SignpostId, string> = {
    your_clips: 'Pick up where you left off',
    favorites: 'Pick up where you left off',
    podcast: 'Podcast Clipping',
    work_with_library: 'Edit with an Agent',
    find_moments: 'Find any moment',
    add_media: 'Bring your media',
  };
  const renderedSignposts: SignpostId[] = [
    ...(showContinue && (clipsLoading || recentClips.length > 0) ? ['your_clips' as const] : []),
    ...(showContinue && recentFavorites.length > 0 ? ['favorites' as const] : []),
    'podcast',
    ...(activeLibrary ? (['work_with_library', 'find_moments'] as const) : []),
    'add_media',
  ];
  const trackSignpost = (signpost: SignpostId, props?: SignpostProps) => {
    trackingStore.workspaceSignpostClicked(signpost, {
      title: signpostTitles[signpost],
      position: renderedSignposts.indexOf(signpost) + 1,
      variant: layoutVariant,
      ...props,
    });
  };

  // Single-select picker in the section header, retargeting the agent and search cards. No
  // "+ New library" row — both cards only act on existing libraries —
  // and an empty library is disabled, since both verbs read media (unlike `AddMediaModal`,
  // which targets a library to upload *into*).
  const libraryPicker = (lib: LibraryStore) => (
    <div className={css.pickerRow}>
      <span className={css.pickerLabel}>Working with</span>
      <LibrarySwitch
        mode="destination"
        allowNewLibrary={false}
        compactTrigger
        disabledReasonFor={emptyLibraryDisabledReason}
        destination={{ kind: 'existing', library: lib }}
        onDestinationChange={(next: LibraryDestination) => {
          if (next.kind === 'existing') {
            setPickedLibraryId(next.library.libraryId);
            trackingStore.workspaceLibraryPicked({
              libraryId: next.library.libraryId,
              source: 'work_with_library',
            });
          }
        }}
      />
    </div>
  );

  const capabilityCards: ReactElement[] = [
    <CapabilityCard
      key="podcast"
      tone="clipper"
      title="Podcast Clipping"
      badges={
        <>
          <span className={`${css.badge} ${css.badgeNew}`}>NEW</span>
          <span className={`${css.badge} ${css.badgeBeta}`}>BETA</span>
        </>
      }
      description="Turn a long episode into short clips."
      preview={<PodcastPreview />}
      disabled={!CHAT_CONFIGURED}
      onClick={() => {
        trackSignpost('podcast', { action: 'clip' });
        const url = getPodcastCreateUrl();
        if (url)
          void openAuthedChatUrl(url, '_self', {
            surface: 'workspace',
            control: 'podcast-signpost',
          });
      }}
    />,
  ];

  if (activeLibrary) {
    capabilityCards.push(
      <CapabilityCard
        key="work_with_library"
        title="Edit with an Agent"
        description="Say what you want made."
        preview={<AgentPreview />}
        onClick={() => {
          trackSignpost('work_with_library', {
            action: 'agent',
            libraryId: activeLibrary.libraryId,
          });
          // Through the hook, not `askAgent` directly, so this card is counted in
          // `Library Verb Clicked` like every other agent button. The signpost event answers a
          // different question ("which workspace card got used").
          workWithAgent(activeLibrary.libraryId, {
            surface: 'workspace',
            control: 'work-with-library-signpost',
          });
        }}
      />,
      <CapabilityCard
        key="find_moments"
        title="Find any moment"
        description={
          activeIsDemo
            ? `Try it on ${demoCount != null ? demoCount.toLocaleString() : '1,000+'} demo media.`
            : 'Search by a line or a scene.'
        }
        preview={<SearchPreview />}
        onClick={() => {
          // Mode is NOT decided here — `findMoments` owns it, so a transcript-only library lands
          // in transcript mode. It comes back for the signpost event so the property reports the
          // search that actually happened.
          const mode = findMoments(activeLibrary.libraryId, {
            surface: 'workspace',
            control: 'find-moments-signpost',
          });
          trackSignpost('find_moments', {
            action: 'search',
            mode,
            libraryId: activeLibrary.libraryId,
          });
        }}
      />,
    );
  }

  capabilityCards.push(
    <CapabilityCard
      key="add_media"
      title="Bring your media"
      description="Upload files or add links."
      preview={<AddMediaPreview />}
      disabled={!defaultLibrary}
      onClick={() => {
        trackSignpost('add_media', { action: 'add' });
        addMediaModalRef.current?.open();
      }}
    />,
  );

  return (
    <>
      <WorkspaceHero />

      {showContinue && (
        <section className={css.section}>
          <div className={css.sectionHeader}>
            <h2 className={css.sectionHeading}>Pick up where you left off</h2>
          </div>
          <ContinueRow
            clips={clipsLoading ? null : recentClips}
            libraries={recentFavorites}
            onSeeAllClips={
              recentClips.length > 0
                ? () => {
                    trackSignpost('your_clips', { action: 'see_all' });
                    void openAuthedChatUrl(getPodcastsLandingUrl(), '_self', {
                      surface: 'workspace',
                      control: 'your-clips-see-all',
                    });
                  }
                : undefined
            }
            onSeeAllLibraries={
              recentFavorites.length > 0
                ? () => {
                    trackSignpost('favorites', { action: 'see_all' });
                    navigate('/libraries');
                  }
                : undefined
            }
            onOpenClip={(project, index) => {
              trackSignpost('your_clips', { action: 'open', itemIndex: index + 1 });
              const href = getPodcastProjectUrl(project._id);
              if (href)
                void openAuthedChatUrl(href, '_self', {
                  surface: 'workspace',
                  control: 'your-clips-item',
                  podcastId: project._id,
                  itemIndex: index + 1,
                });
            }}
            onOpenLibrary={(lib, index) => {
              trackSignpost('favorites', {
                libraryId: lib.libraryId,
                action: 'open',
                itemIndex: index + 1,
              });
              navigate(getLibraryUrl(lib.libraryId, lib.name || ''));
            }}
          />
        </section>
      )}

      <section className={`${css.section} ${css.fillSection}`}>
        <div className={css.sectionHeader}>
          {/* Waits for library stats like the cards do: until they land everyone reads as a
              first-time user, and a returning user would see "Start here" flip. The header
              keeps its height meanwhile, so nothing shifts when it fills in. */}
          {libraryManager.areLibraryStatsLoaded && (
            <h2 className={css.sectionHeading}>
              {isFirstTimeUser ? 'Start here' : 'Start something new'}
            </h2>
          )}
          {libraryManager.areLibraryStatsLoaded && activeLibrary && (
            <div className={css.headerAside}>
              {awaitingOwnMedia && (
                <span className={css.headerNote}>
                  Your media is still processing. Try it on the demo meanwhile.
                </span>
              )}
              {libraryPicker(activeLibrary)}
            </div>
          )}
        </div>
        <div
          className={css.cards}
          // One column per card actually shown, so a missing demo library (no agent or search
          // card) doesn't leave empty slots in the row.
          style={
            libraryManager.areLibraryStatsLoaded
              ? ({ '--card-count': capabilityCards.length } as CSSProperties)
              : undefined
          }
        >
          {libraryManager.areLibraryStatsLoaded ? capabilityCards : <CapabilitySkeletons />}
        </div>
      </section>

      {/* No `destination` → AddMediaModal targets the default library. The card stays
          disabled until that library has loaded. */}
      <AddMediaModal ref={addMediaModalRef} />
    </>
  );
});

const WorkspaceContent = observer(() => {
  const navigate = useNavigate();
  const libraryManager = useLibraryManager();
  const librarySearch = libraryManager.multiLibSearch;

  // Clear any stale search text when workspace mounts
  const isInitialMount = useRef(true);

  // Navigate to /search when user types in the SearchBar
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Clear lingering search text so we don't redirect on mount
      if (librarySearch.request?.text) {
        librarySearch.setSearchRequest(null);
      }
      return;
    }

    const text = librarySearch.request?.text?.trim();
    if (text) {
      const params = new URLSearchParams();
      params.set('text', text);

      const libs = Array.from(libraryManager.selectedLibraryIdsToSearch).join(',');
      if (libs) params.set('libs', libs);

      params.set('search', librarySearch.resultType);
      params.set('from', 'workspace');

      navigate(`/search?${params.toString()}`);
    }
  }, [librarySearch.request?.text]);

  return (
    <div className={css.container}>
      <div className={css.scrollableContent}>
        <div className={css.content}>
          <WorkspaceHome />
        </div>
      </div>
    </div>
  );
});

export const WorkspacePage = observer(function WorkspacePage() {
  // Unverified users get the verify-email notice instead (guard on an explicit
  // `false` so we don't flash it while `userStore.data` is still loading).
  const unverified = userStore.data && !userStore.data.isVerified;
  return (
    <AppPage header="Workspace" floatingHeader>
      {unverified ? <UnverifiedNotice /> : <WorkspaceContent />}
    </AppPage>
  );
});
